/**
 * The chart layer's colours and chrome, as one place.
 *
 * **Every value here was run through the palette validator against this app's own surfaces** —
 * `gray-100` in light and `zinc-800` in dark, which is what `bg-depth` resolves to inside a
 * `widget`. That matters: contrast and colour-vision separation are only meaningful against the
 * surface a chart actually renders on, and the two surfaces here are both a step away from the
 * validator's defaults. Both palettes pass the lightness band, the chroma floor, adjacent CVD
 * separation and the normal-vision floor.
 *
 * Four of the light slots sit below 3:1 against `gray-100`, which is a **relief obligation** rather
 * than a licence: every chart in this feature ships either direct labels or the table the chart was
 * drawn from, so identity never rests on hue alone. That is also why a legend is present for any
 * chart with two or more series.
 *
 * The ordering is the colour-vision safety mechanism, not a preference. Slots are assigned in this
 * order and **never cycled**: a ninth series folds into "Other" rather than reusing slot 1, because
 * two entities sharing a colour is worse than one entity being aggregated.
 */

/** Light-mode categorical slots, in assignment order. */
const CATEGORICAL_LIGHT = [
	'#2a78d6',
	'#eb6834',
	'#1baf7a',
	'#eda100',
	'#e87ba4',
	'#008300',
	'#4a3aa7',
	'#e34948'
];

/** The same eight hues stepped for the dark surface — not a flip, and not a different palette. */
const CATEGORICAL_DARK = [
	'#3987e5',
	'#d95926',
	'#199e70',
	'#c98500',
	'#d55181',
	'#008300',
	'#9085e9',
	'#e66767'
];

export interface ChartTheme {
	dark: boolean;
	series: string[];
	surface: string;
	gridline: string;
	axis: string;
	mutedInk: string;
	primaryInk: string;
	/** The one hue a single-series chart uses, and the funnel's bars. */
	single: string;
}

/**
 * The theme in force.
 *
 * Read from the `dark` class on `<html>`, which is where `AbstractID.applyTheme` puts it, rather
 * than from `prefers-color-scheme`: the app has an explicit toggle, and a chart that followed the
 * OS while the page followed the toggle would be the only element on screen in the wrong mode.
 */
export function chartTheme(): ChartTheme {
	const dark =
		typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
	return {
		dark,
		series: dark ? [...CATEGORICAL_DARK] : [...CATEGORICAL_LIGHT],
		// `bg-depth` level 1: gray-100 light, zinc-800 dark. The surfaces the palette was validated
		// against, and the ground the tooltip has to sit legibly on.
		surface: dark ? '#27272a' : '#f4f4f5',
		gridline: dark ? '#3f3f46' : '#e4e4e7',
		axis: dark ? '#52525b' : '#d4d4d8',
		mutedInk: dark ? '#a1a1aa' : '#71717a',
		primaryInk: dark ? '#fafafa' : '#18181b',
		single: dark ? CATEGORICAL_DARK[0] : CATEGORICAL_LIGHT[0]
	};
}

/**
 * One series' colour.
 *
 * Keyed by **position in a stable ordering of the entity**, never by rank in the current result:
 * a filter that drops one series must not repaint the survivors, or a reader comparing two
 * screenshots concludes the data changed when only the filter did.
 */
export function seriesColor(index: number, theme: ChartTheme): string {
	return theme.series[index % theme.series.length];
}

/**
 * The chrome every chart shares.
 *
 * Recessive grid and axes, no toolbar (it offers a PNG export of a chart the page already lets you
 * read as a table), no animation on update — a chart that re-animates on every filter change makes
 * comparing two filters a memory test.
 */
export function baseChartOptions(theme: ChartTheme, height: number): Record<string, unknown> {
	return {
		chart: {
			height,
			fontFamily: 'inherit',
			background: 'transparent',
			toolbar: { show: false },
			zoom: { enabled: false },
			animations: { enabled: false }
		},
		theme: { mode: theme.dark ? 'dark' : 'light' },
		grid: {
			borderColor: theme.gridline,
			strokeDashArray: 0,
			padding: { left: 8, right: 8, top: 0, bottom: 0 }
		},
		tooltip: { theme: theme.dark ? 'dark' : 'light', shared: true, intersect: false },
		dataLabels: { enabled: false },
		legend: {
			position: 'bottom',
			horizontalAlign: 'left',
			fontSize: '12px',
			markers: { width: 10, height: 10, radius: 3 },
			labels: { colors: theme.mutedInk }
		},
		xaxis: {
			axisBorder: { color: theme.axis },
			axisTicks: { color: theme.axis },
			labels: { style: { colors: theme.mutedInk, fontSize: '11px' } }
		},
		yaxis: {
			labels: { style: { colors: theme.mutedInk, fontSize: '11px' } }
		}
	};
}
